'use memo';

import React from 'react';
import dayjs from 'dayjs';
import {
  AgxColumn,
  Images,
  AgxBodyText,
  AgxButton,
  useWindowSize,
  Breakpoints,
} from '@urbanx/agx-ui-components';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import placeHolderImage from 'assets/images/placeholder-property.png';

import ContractCard from './ContractCard';
import BlankContractProgress from './BlankContractProgress';
import './ContractDetails.scss';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { SolicitorContractDetails } from './SolicitorContractDetails';
import {
  CampaignCompletionState,
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractDetailModel,
} from '@urbanx/agx.campaigns.client';
import { shouldShowBlankContractProgress } from './contractDetailsDisplay';

interface ContractDetailsProps {
  campaign: CampaignDetailModel;
  contractDetails: ContractDetailModel[];
  completionState: CampaignCompletionState;
  propertyImageUrls: { thumbnail: string | undefined };
  mobile?: boolean;
}

const sortByDate = (a: ContractDetailModel, b: ContractDetailModel) =>
  dayjs(b.lastUpdate).diff(dayjs(a.lastUpdate));

const ContractDetails: React.FC<ContractDetailsProps> = ({
  campaign,
  contractDetails = [],
  completionState,
  propertyImageUrls: imageUrls,
  mobile = false,
}) => {
  const screenSize = useWindowSize();
  const getFileLink = useGetFileLink();
  const { automateLiveListings } = useFeatureToggles(campaign?.state);
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;
  const showBlankContractProgress = shouldShowBlankContractProgress(
    campaign?.state,
    automateLiveListings
  );

  const onDownloadBlankContract = async () => {
    if (campaign.blankSalesContract) {
      const fileLink = await getFileLink(
        campaign.blankSalesContract.containerFilePath
      );
      if (fileLink != null) {
        openFileInNewTab(screenSize === Breakpoints.Desktop, fileLink);
      }
    }
  };

  const noContracts = isNsw ? (
    <SolicitorContractDetails campaign={campaign} />
  ) : showBlankContractProgress ? (
    <BlankContractProgress
      blankContractProgress={campaign.blankContractProgress ?? {}}
    />
  ) : (
    <AgxColumn fill centered centerJustified mediumGap>
      <Images.SparkleForm />
      <AgxBodyText medium>You haven't created a contract yet!</AgxBodyText>
      <AgxBodyText medium>When you do, they'll appear here.</AgxBodyText>
    </AgxColumn>
  );

  const allContracts = contractDetails
    .map((contract: ContractDetailModel) => {
      return {
        ...contract,
        contractStage: contract.contractStage,
        formType: contract.inProgressForm?.formType,
      };
    })
    .sort(sortByDate)
    .map((contract, idx) => (
      <ContractCard
        key={idx}
        state={campaign.state ?? CampaignDetailModelState.QLD}
        contract={contract}
        isArchived={!!completionState?.completionType}
        propertyImage={imageUrls?.thumbnail ?? placeHolderImage}
      />
    ));

  const showAllContracts = allContracts.length > 0;

  return (
    <>
      <AgxColumn
        extraClasses={
          isNsw ? 'solicitorRequestingContracts' : 'contractRequestContracts'
        }
        mediumGap
        fill={!showAllContracts && !showBlankContractProgress}
        centered={!showAllContracts && !showBlankContractProgress}
        centerJustified={!showAllContracts && !showBlankContractProgress}
      >
        {showAllContracts ? allContracts : noContracts}
      </AgxColumn>

      {campaign.blankContractIsLive && campaign.blankSalesContract && (
        <AgxColumn extraLargePadding>
          <AgxButton
            dataTestId="agx-download-blank-contract"
            hollow
            large={!mobile}
            medium={mobile}
            wide
            text={`Download ${campaign?.blankSalesContractStatus ?? 'Blank'} Contract`}
            onClick={onDownloadBlankContract}
          />
        </AgxColumn>
      )}
    </>
  );
};

export default ContractDetails;
